<style lang="less" scoped>
@import "/styles/SiteColors.less";
.input-group {
    & > ::v-deep(.form-control:not(:first-child)) {
        border-top-left-radius: 0.25rem;
        border-bottom-left-radius: 0.25rem;
    }
    .input-group-text {
        padding: 0.25rem;
        font-size: larger;
        #jsPageEditor & {
            padding: 0.35rem;
            font-size: 22px;
            border-color: @primary-lightest;
        }
    }
}
</style>
<template>
    <div class="input-group datepicker-group">
        <!-- <pre>
            value: {{value}}
            tempValue: {{tempValue}}
            altInputValue: {{altInputValue}}
        </pre> -->
        <flat-pickr ref="flatPickrEl" v-model="tempValue" v-bind="$attrs" @on-close="emitValue" @change="emitValue" :config="effectiveConfig" />
        <div class="input-group-append">
            <button ref="datepickerToggle" @click="openDatepicker" class="input-group-text icon icon-lg-calendar" type="button" aria-label="Open datepicker" aria-expanded="false" />
        </div>
    </div>
</template>

<script lang="ts">
//cspell:ignore PAGEUP, Dayof
import { defineComponent, onMounted, ref, computed, SetupContext, watch } from "vue";
import { usePageStore } from "../public/pages/pageState/store";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { isNullOrWhitespace, stringCoalesce } from "@/lib/whitespaceMethods";
import moment from "moment";
interface Props {
    modelValue?: string | null;
    ariaLabelledBy?: string;
    ariaRequired?: boolean;
    ariaDescribedBy?: string;
    vmId?: string;
    defaultValue?: string;
    placeholder?: string;
    config: {
        allowInput: boolean;
        altInput: boolean;
        dateFormat: string;
        altFormat: string;
        disableMobile: boolean;
        defaultDate: string;
        onOpen: () => void;
        onClose: () => void;
    };
}
export default defineComponent({
    props: {
        modelValue: {
            type: String,
            required: false,
        },
        ariaLabelledBy: {
            type: String,
            required: false,
        },
        ariaRequired: {
            type: Boolean,
            required: false,
        },
        ariaDescribedBy: {
            type: String,
            required: false,
        },
        vmId: {
            type: String,
            required: false,
        },
        defaultValue: {
            type: String,
            required: false,
        },
        placeholder: {
            type: String,
            required: false,
        },
        config: {
            type: Object,
            required: false,
            default() {
                return {
                    allowInput: true,
                    altInput: true,
                    dateFormat: "m-d-Y",
                    altFormat: "m-d-Y",
                    disableMobile: true,
                    defaultDate: "",
                    onOpen: () => {},
                    onClose: () => {},
                };
            },
        },
    },
    components: {
        "flat-pickr": FlatPickr,
    },
    setup(props: Props, context: SetupContext) {
        const flatPickrEl = ref<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
        const pageStore = usePageStore();
        const tempValue = ref<string>("");
        const altInputValue = ref<string>("");
        const datepickerToggle = ref<Element>(null!);

        const effectiveConfig = computed(() => {
            const getValueVal = getValue();
            return {
                ...props.config,
                wrap: true,
                clickOpens: false,
                defaultDate: stringCoalesce(...(Array.isArray(getValueVal) ? getValueVal : [getValueVal || ""]), props.defaultValue, props.config.defaultDate),
                onOpen: (_1: string[], selectedDate: string, flatpickrInstance: typeof flatPickr.value) => {
                    if (props.config.onOpen) {
                        props.config.onOpen();
                    }
                    flatpickrInstance.jumpToDate(selectedDate || undefined);
                    datepickerToggle.value.setAttribute("aria-expanded", "true");
                },
                onClose: (_1: string[], selectedDate: string, _flatpickrInstance: typeof flatPickr.value) => {
                    altInputValue.value = selectedDate;
                    datepickerToggle.value.setAttribute("aria-expanded", "false");
                    (datepickerToggle.value as HTMLElement).focus();
                    if (props.config.onClose) {
                        props.config.onClose();
                    }
                },
            };
        });
        const flatPickr = computed(() => {
            return flatPickrEl.value ? flatPickrEl.value.fp : null;
        });

        const getValue = () => {
            if (props.modelValue !== undefined) {
                return props.modelValue;
            } else if (props.vmId !== undefined) {
                return pageStore.getValueByVmId(props.vmId);
            }
        };
        const setPlaceholder = () => {
            const effectivePlaceholder = stringCoalesce(props.defaultValue, props.placeholder, null);
            if (effectivePlaceholder != null) {
                flatPickr.value._input.setAttribute("placeholder", effectivePlaceholder);
            } else {
                flatPickr.value._input.removeAttribute("placeholder");
            }
            if (!isNullOrWhitespace(props.defaultValue)) {
                flatPickr.value._input.classList.add("dfDefaultValue");
            } else {
                flatPickr.value._input.classList.remove("dfDefaultValue");
            }
        };
        const emitValue = () => {
            if (context.attrs.onHtmlInput) {
                context.emit("html-input", { target: flatPickr.value._input } as Event);
            } else {
                context.emit("update:modelValue", flatPickr.value._input.value);
            }
        };
        const setAltInputValue = (event: KeyboardEvent) => {
            altInputValue.value = (event.target as HTMLInputElement).value;
            if (!altInputValue.value.length) {
                flatPickr.value.clear();
            }
        };
        const setDate = () => {
            if (altInputValue.value.length === 10 && flatPickr.value.parseDate(altInputValue.value) !== undefined) {
                flatPickr.value.setDate(altInputValue.value);
            }
        };
        const openDatepicker = async () => {
            if (!flatPickr.value.isOpen) {
                await flatPickr.value.open();
            } else {
                flatPickr.value.close();
            }
            const elementToFocus = flatPickr.value.calendarContainer.querySelector(".flatpickr-day.selected") || flatPickr.value.calendarContainer.querySelector(".flatpickr-day.today");
            if (elementToFocus?.classList.contains("selected")) {
                elementToFocus.setAttribute("aria-description", "selected");
            }
            elementToFocus.focus();
        };
        const calendarKeydownHandler = async (event: KeyboardEvent) => {
            /*  Key               event.key
             * Backspace        "Backspace"
             * Tab                    "Tab"
             * Enter                "Enter"
             * Escape              "Escape"
             * Spacebar                 " "
             * Page Up             "PageUp"
             * Page Down         "PageDown"
             * End                    "End"
             * Home                  "Home"
             * Arrow Left       "ArrowLeft"
             * Arrow Up           "ArrowUp"
             * Arrow Right     "ArrowRight"
             * Arrow Down       "ArrowDown"
             * Delete              "Delete"
             */
            if (["Backspace", "Tab", "Enter", "Escape", " ", "PageUp", "PageDown", "End", "Home", "ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Delete"].includes(event.key)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
            const momentDate = moment({
                year: flatPickr.value.currentYear,
                month: flatPickr.value.currentMonth,
                day: parseInt(document.activeElement?.textContent || ""),
            });
            let ariaLabel;

            switch (event.key) {
                // Close datepicker by ESCAPE or TAB key
                case "Escape":
                case "Tab": {
                    if (document.activeElement != flatPickr.value._input) {
                        flatPickr.value._input.focus();
                    }
                    flatPickr.value.close();
                    break;
                }
                // Clear datepicker value by BACKSPACE or DELETE key
                case "Backspace":
                case "Delete": {
                    flatPickr.value.clear();
                    await new Promise(resolve => setTimeout(resolve, 0));
                    flatPickr.value.todayDateElem.focus();
                    break;
                }
                // Set date by ENTER or SPACEBAR key
                case "Enter":
                case " ": {
                    flatPickr.value.setDate(new Date(flatPickr.value.currentYear, flatPickr.value.currentMonth, parseInt(document.activeElement?.textContent || "")));
                    if (event.key == "Enter") {
                        flatPickr.value._input.focus();
                        flatPickr.value.close();
                    } else {
                        flatPickr.value.selectedDateElem.setAttribute("aria-description", "selected");
                        flatPickr.value.selectedDateElem.focus();
                    }
                    break;
                }
                // Navigate month or year by PAGEUP or PAGEDOWN key
                case "PageUp":
                case "PageDown": {
                    const isPrevious = event.key == "PageUp";
                    ariaLabel = flatPickr.value.formatDate(momentDate[isPrevious ? "subtract" : "add"](1, event.shiftKey ? "years" : "months").toDate(), flatPickr.value.config.ariaDateFormat);
                    flatPickr.value[event.shiftKey ? "changeYear" : "changeMonth"]((event.shiftKey ? flatPickr.value.currentYear : 0) + (isPrevious ? -1 : 1));
                    flatPickr.value.calendarContainer.querySelector(`.flatpickr-day[aria-label="${ariaLabel}"]`).focus();
                    break;
                }
                // Select the first or last day of the week by HOME or END key
                case "Home":
                case "End": {
                    const isFirstDayofWeek = event.key == "Home";
                    ariaLabel = flatPickr.value.formatDate(momentDate[isFirstDayofWeek ? "startOf" : "endOf"]("week").toDate(), flatPickr.value.config.ariaDateFormat);
                    flatPickr.value.calendarContainer.querySelector(`.flatpickr-day[aria-label="${ariaLabel}"]`).focus();
                    break;
                }
                // Navigate date daily by ARROW LEFT or RIGHT key
                case "ArrowLeft":
                case "ArrowRight": {
                    const isPreviousDay = event.key == "ArrowLeft";
                    const updatedDayDate = momentDate[isPreviousDay ? "subtract" : "add"](1, "days").toDate();
                    ariaLabel = flatPickr.value.formatDate(updatedDayDate, flatPickr.value.config.ariaDateFormat);

                    flatPickr.value.jumpToDate(updatedDayDate);
                    flatPickr.value.calendarContainer.querySelector(`.flatpickr-day[aria-label="${ariaLabel}"]`).focus();
                    break;
                }
                // Navigate date weekly by ARROW UP or DOWN key
                case "ArrowUp":
                case "ArrowDown": {
                    const isPreviousWeek = event.key == "ArrowUp";
                    const updatedWeekDate = momentDate[isPreviousWeek ? "subtract" : "add"](1, "weeks").toDate();
                    ariaLabel = flatPickr.value.formatDate(updatedWeekDate, flatPickr.value.config.ariaDateFormat);

                    flatPickr.value.jumpToDate(updatedWeekDate);
                    flatPickr.value.calendarContainer.querySelector(`.flatpickr-day[aria-label="${ariaLabel}"]`).focus();
                    break;
                }
                default:
                    break;
            }
        };

        onMounted(() => {
            if (flatPickr.value !== undefined) {
                flatPickr.value._input.addEventListener(
                    "blur",
                    () => {
                        setDate();
                        emitValue();
                    },
                    true,
                );
                flatPickr.value._input.addEventListener("input", (event: KeyboardEvent) => setAltInputValue(event));
                flatPickr.value.daysContainer.addEventListener("keydown", calendarKeydownHandler);

                flatPickr.value._input.setAttribute("aria-labelledby", props.ariaLabelledBy || context.attrs.id);
                flatPickr.value._input.setAttribute("aria-required", props.ariaRequired);
                flatPickr.value._input.setAttribute("aria-describedby", props.ariaDescribedBy);

                flatPickr.value.calendarContainer.setAttribute("role", "dialog");
                flatPickr.value.calendarContainer.setAttribute("aria-modal", "true");
                flatPickr.value.calendarContainer.setAttribute("aria-label", "Choose Date");
                flatPickr.value.calendarContainer.setAttribute("aria-description", "Cursor keys can navigate dates");

                if (context.attrs.id) {
                    flatPickr.value._input.setAttribute("id", context.attrs.id);
                    flatPickr.value.element.removeAttribute("id");
                }
                setPlaceholder();
            }
        });

        watch(
            () => props.placeholder,
            () => {
                setPlaceholder();
            },
        );
        watch(
            () => props.defaultValue,
            () => {
                setPlaceholder();
            },
        );
        watch(
            () => tempValue.value,
            tempVal => {
                if (props.modelValue != tempVal) {
                    context.emit("update:modelValue", tempVal);
                }
            },
        );
        watch(
            () => props.modelValue,
            val => {
                if (tempValue.value != val) {
                    tempValue.value = val ?? "";
                }
            },
        );
        watch(
            () => altInputValue.value,
            () => setDate(),
        );
        watch(
            () => props.ariaDescribedBy,
            () => flatPickr.value._input.setAttribute("aria-describedby", props.ariaDescribedBy),
        );

        return {
            // Ref
            datepickerToggle,

            // States
            tempValue,

            // Computed
            effectiveConfig,
            flatPickr,

            // Methods
            emitValue,
            openDatepicker,

            // Ref
            flatPickrEl,
        };
    },
});
</script>
